
import { Component, Vue } from 'vue-property-decorator'
import { Action, Getter } from 'vuex-class'
import BaseLayout from '@/shared/components/BaseLayout.vue'
import CategoryMappingService, { CategoryMappingItem, MarketCategorySearchResult, RetailCrmCategoryGroup } from '@/api/services/CategoryMappingService'
import Settings from '@/api/models/Settings'
import { getAccountId, getCampaignId } from '@/service/storage'
import { RootStateMutations } from '@/store/mutations'

interface CategoryMappingRow {
    key: string;
    group: RetailCrmCategoryGroup | null;
    mapping: CategoryMappingItem | null;
}

const PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300
const categoryMappingService = new CategoryMappingService()

@Component({
    components: {
        BaseLayout
    }
})
export default class CategoryMappings extends Vue {
    @Getter('currentAccount')
    account!: number | undefined

    @Getter('currentCampaign')
    campaign!: number

    @Getter('settings')
    settings!: Settings

    @Action('loadSettingsBase')
    loadSettingsBase!: () => Promise<void>

    groups: RetailCrmCategoryGroup[] = []

    mappings: CategoryMappingItem[] = []

    currentPage = 1

    editingGroup: string | null = null

    loading = true

    loadFailed = false

    settingsLoaded = false

    searchQuery = ''

    searchResults: MarketCategorySearchResult[] = []

    draftCategory: MarketCategorySearchResult | null = null

    pendingRows: string[] = []

    private searchTimer: number | null = null

    private searchRequestId = 0

    created (): void {
        if (!this.account) {
            this.$store.commit(RootStateMutations.setAccount, getAccountId())
        }
        if (!this.campaign) {
            this.$store.commit(RootStateMutations.setCampaign, getCampaignId())
        }
    }

    get gateMessage (): string | null {
        const productsDisabled = !this.settings.exchangeProducts
        const businessIdMissing = !this.settings.businessId

        if (productsDisabled && businessIdMissing) {
            return 'categoryMappings.gate.both'
        }
        if (productsDisabled) {
            return 'categoryMappings.gate.exchangeProducts'
        }
        if (businessIdMissing) {
            return 'categoryMappings.gate.businessId'
        }

        return null
    }

    get rows (): CategoryMappingRow[] {
        const groupsById = new Map(this.groups.map((group) => [group.id, group]))
        const mappingsById = new Map(this.mappings.map((mapping) => [mapping.retailCrmCategoryId, mapping]))
        const rows = this.groups
            .map((group) => ({ key: `group-${group.id}`, group, mapping: mappingsById.get(group.id) ?? null }))

        this.mappings.forEach((mapping) => {
            if (!groupsById.has(mapping.retailCrmCategoryId)) {
                rows.push({
                    key: `orphan-${mapping.retailCrmCategoryId}`,
                    group: null,
                    mapping
                })
            }
        })

        return rows
    }

    get pageCount (): number {
        return Math.ceil(this.rows.length / PAGE_SIZE)
    }

    get showPagination (): boolean {
        return this.rows.length > PAGE_SIZE
    }

    get pageRows (): CategoryMappingRow[] {
        const start = (this.currentPage - 1) * PAGE_SIZE
        return this.rows.slice(start, start + PAGE_SIZE)
    }

    async mounted (): Promise<void> {
        await (this.$refs.baseLayout as BaseLayout).handleRequest({
            tryBody: async () => {
                try {
                    await this.loadSettingsBase()
                    this.settingsLoaded = true
                    if (this.gateMessage) {
                        return
                    }

                    const response = await categoryMappingService.getCategoryMappings(this.account, this.campaign)
                    this.groups = response.groups
                    this.mappings = response.mappings
                } catch (error) {
                    this.loadFailed = true
                    throw error
                }
            }
        })
        this.loading = false
    }

    beforeDestroy (): void {
        if (this.searchTimer !== null) {
            window.clearTimeout(this.searchTimer)
            this.searchTimer = null
        }
        this.searchRequestId++
    }

    openEditor (group: string): void {
        if (this.isSaving()) {
            return
        }
        this.resetSearch()
        this.editingGroup = group
    }

    closeEditor (): void {
        this.resetSearch()
        this.editingGroup = null
    }

    searchCategories (event: Event): void {
        this.searchQuery = (event.target as HTMLInputElement).value
        this.searchResults = []
        const requestId = ++this.searchRequestId

        if (this.searchTimer !== null) {
            window.clearTimeout(this.searchTimer)
            this.searchTimer = null
        }

        const query = this.searchQuery.trim()
        if (query.length < 3) {
            return
        }

        this.searchTimer = window.setTimeout(async () => {
            this.searchTimer = null
            try {
                const results = await categoryMappingService.searchCategories(this.account, this.campaign, query)
                if (requestId === this.searchRequestId && query === this.searchQuery.trim()) {
                    this.searchResults = results
                }
            } catch (error) {
                if (requestId === this.searchRequestId) {
                    await (this.$refs.baseLayout as BaseLayout).handleRequest({ tryBody: async () => { throw error } })
                }
            }
        }, SEARCH_DEBOUNCE_MS)
    }

    selectCategory (category: MarketCategorySearchResult): void {
        this.draftCategory = category
        this.searchResults = []
    }

    isPending (key: string): boolean {
        return this.pendingRows.includes(key)
    }

    isSaving (): boolean {
        return this.pendingRows.length > 0
    }

    async saveMapping (row: CategoryMappingRow): Promise<void> {
        if (!row.group || !this.draftCategory || this.isPending(row.key)) {
            return
        }

        const group = row.group
        const draft = this.draftCategory
        if (!group || !draft) {
            return
        }
        this.pendingRows.push(row.key)
        try {
            await (this.$refs.baseLayout as BaseLayout).handleRequest({
                tryBody: async () => {
                    await categoryMappingService.saveCategoryMapping(this.account, this.campaign, group.id, draft.marketCategoryId)
                    const mapping: CategoryMappingItem = {
                        retailCrmCategoryId: group.id,
                        marketCategoryId: draft.marketCategoryId,
                        marketCategory: draft,
                        valid: true
                    }
                    const index = this.mappings.findIndex(item => item.retailCrmCategoryId === group.id)
                    if (index === -1) {
                        this.mappings.push(mapping)
                    } else {
                        this.$set(this.mappings, index, mapping)
                    }
                    this.closeEditor()
                }
            })
        } finally {
            this.pendingRows = this.pendingRows.filter(key => key !== row.key)
        }
    }

    async deleteMapping (row: CategoryMappingRow): Promise<void> {
        const mapping = row.mapping
        if (!mapping || this.isPending(row.key)) {
            return
        }

        this.pendingRows.push(row.key)
        try {
            await (this.$refs.baseLayout as BaseLayout).handleRequest({
                tryBody: async () => {
                    await categoryMappingService.deleteCategoryMapping(this.account, this.campaign, mapping.retailCrmCategoryId)
                    this.mappings = this.mappings.filter(item => item.retailCrmCategoryId !== mapping.retailCrmCategoryId)
                    this.currentPage = Math.min(Math.max(1, this.currentPage), Math.max(1, this.pageCount))
                    if (this.editingGroup === row.key) {
                        this.closeEditor()
                    }
                }
            })
        } finally {
            this.pendingRows = this.pendingRows.filter(key => key !== row.key)
        }
    }

    private resetSearch (): void {
        if (this.searchTimer !== null) {
            window.clearTimeout(this.searchTimer)
            this.searchTimer = null
        }
        this.searchRequestId++
        this.searchQuery = ''
        this.searchResults = []
        this.draftCategory = null
    }

    previousPage (): void {
        this.currentPage = Math.max(1, this.currentPage - 1)
    }

    nextPage (): void {
        this.currentPage = Math.min(this.pageCount, this.currentPage + 1)
    }
}
